class WatchAndBuy extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-watch-buy-track]');
    if (!this.track || this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';
    this.cards = [...this.track.querySelectorAll('[data-watch-buy-card]')];
    this.videos = [...this.track.querySelectorAll('video')];
    this.count = this.querySelector('[data-watch-buy-count]');
    this.fill = this.querySelector('[data-watch-buy-fill]');
    this.prev = this.querySelector('[data-watch-buy-prev]');
    this.next = this.querySelector('[data-watch-buy-next]');
    this.abortController = new AbortController();
    this.frame = 0;
    this.drag = null;

    const signal = this.abortController.signal;
    this.track.addEventListener('scroll', () => this.requestUpdate(), { passive: true, signal });
    this.track.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
    this.track.addEventListener('pointerdown', (event) => this.handlePointerDown(event), { signal });
    this.track.addEventListener('pointermove', (event) => this.handlePointerMove(event), { signal });
    this.track.addEventListener('pointerup', (event) => this.handlePointerUp(event), { signal });
    this.track.addEventListener('pointercancel', (event) => this.handlePointerUp(event), { signal });
    window.addEventListener('resize', () => this.requestUpdate(), { passive: true, signal });
    this.prev?.addEventListener('click', () => this.step(-1), { signal });
    this.next?.addEventListener('click', () => this.step(1), { signal });

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(([entry]) => this.setPlayback(entry.isIntersecting), {
        threshold: 0.15,
      });
      this.observer.observe(this);
    } else {
      this.setPlayback(true);
    }

    this.update();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
    this.dataset.initialized = 'false';
  }

  requestUpdate() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.update();
    });
  }

  update() {
    const maximum = Math.max(1, this.track.scrollWidth - this.track.clientWidth);
    const progress = Math.min(1, Math.max(0, this.track.scrollLeft / maximum));

    // An arrow with nothing beyond it is retired rather than left to press
    // for no effect. Subpixel scroll positions never land exactly on 0 or on
    // the maximum, hence the 1px tolerance.
    if (this.prev) this.prev.disabled = this.track.scrollLeft <= 1;
    if (this.next) this.next.disabled = this.track.scrollLeft >= maximum - 1;
    const active = this.cards.length
      ? Math.min(this.cards.length, 1 + Math.round(progress * (this.cards.length - 1)))
      : 0;

    if (this.fill) this.fill.style.transform = `scaleX(${progress.toFixed(4)})`;
    if (this.count) this.count.textContent = `${this.pad(active)} — ${this.pad(this.cards.length)}`;

    // Keep every shoppable card at an identical size. The reference rail's
    // perspective scaling made the first and last visible cards look smaller.
    this.cards.forEach((card) => card.style.removeProperty('transform'));
  }

  pad(number) {
    return String(number).padStart(2, '0');
  }

  handleKeydown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    this.step(event.key === 'ArrowRight' ? 1 : -1);
  }

  /** Move the track by one card. Shared by the arrow keys and the arrow buttons. */
  step(direction) {
    const card = this.cards[0];
    if (!card) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    this.track.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: 'smooth' });
  }

  handlePointerDown(event) {
    if (event.button !== 0 || event.target.closest('button, a')) return;
    this.drag = { id: event.pointerId, x: event.clientX, left: this.track.scrollLeft };
    this.track.setPointerCapture(event.pointerId);
    this.track.classList.add('is-dragging');
  }

  handlePointerMove(event) {
    if (!this.drag || this.drag.id !== event.pointerId) return;
    this.track.scrollLeft = this.drag.left - (event.clientX - this.drag.x);
  }

  handlePointerUp(event) {
    if (!this.drag || this.drag.id !== event.pointerId) return;
    if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
    this.drag = null;
    this.track.classList.remove('is-dragging');
  }

  setPlayback(shouldPlay) {
    this.videos.forEach((video) => {
      if (!shouldPlay) {
        video.pause();
        return;
      }
      const promise = video.play();
      if (promise?.catch) promise.catch(() => {});
    });
  }
}

if (!customElements.get('watch-and-buy')) {
  customElements.define('watch-and-buy', WatchAndBuy);
}
